/**
 * Utility functions for supplier Incoterm locations (TT-4239)
 */

import { SEND_INCOTERM_LOCATION_ID } from "~/constants/incotermLocationConstants";
import { IncotermLocationOption, SelectedIncotermLocation } from "~/types-and-enums/incotermLocationTypes";

type IncotermLocationAddress = Pick<
  SelectedIncotermLocation,
  "street" | "postal_code" | "city" | "country_code" | "country"
>;

/** Address fields as stored on PQs, candidates and stock items (country may be an ISO code or a free-text name). */
type IncotermLocationRowFields = {
  id?: string | null;
  street?: string | null;
  postal_code?: string | null;
  city?: string | null;
  country?: string | null;
};

const ISO_COUNTRY_CODE = /^[A-Za-z]{2}$/;

const trimmed = (value: string | null | undefined) => value?.trim() ?? "";

const getCountry = (location: Partial<IncotermLocationAddress>) =>
  trimmed(location.country_code) || trimmed(location.country);

/** "Hafenstraße 12, 20457 Hamburg, DE"; street and postal code are left out when empty. */
export const buildIncotermLocationLabel = (location: Partial<IncotermLocationAddress>): string => {
  const locality = [trimmed(location.postal_code), trimmed(location.city)].filter(Boolean).join(" ");
  return [trimmed(location.street), locality, getCountry(location)].filter(Boolean).join(", ");
};

/** "20457 Hamburg, DE" / "Hamburg, DE" / "" — for table cells, filters and search values. */
export const formatIncotermLocationShort = (location: Partial<IncotermLocationAddress> | null | undefined): string => {
  if (!location) return "";
  const locality = [trimmed(location.postal_code), trimmed(location.city)].filter(Boolean).join(" ");
  return [locality, getCountry(location)].filter(Boolean).join(", ");
};

export const toSelectedIncotermLocation = ({
  id,
  label,
  street,
  postal_code,
  city,
  country_code,
  country,
}: IncotermLocationOption): SelectedIncotermLocation => ({
  id,
  label,
  street,
  postal_code,
  city,
  country_code,
  country,
});

/**
 * A location from free-text fields that is not linked to an incoterm location record (id: null).
 * A two-letter country is treated as an ISO code. Returns null when city, postal code and country are all empty.
 */
export const legacyIncotermLocation = ({
  street,
  postal_code,
  city,
  country,
}: Omit<IncotermLocationRowFields, "id">): SelectedIncotermLocation | null => {
  const countryValue = trimmed(country);
  if (!trimmed(city) && !trimmed(postal_code) && !countryValue) return null;

  const isCountryCode = ISO_COUNTRY_CODE.test(countryValue);
  const address: IncotermLocationAddress = {
    street: trimmed(street) || null,
    postal_code: trimmed(postal_code) || null,
    city: trimmed(city),
    country_code: isCountryCode ? countryValue.toUpperCase() : "",
    country: isCountryCode ? null : countryValue || null,
  };
  return { id: null, label: buildIncotermLocationLabel(address), ...address };
};

/** The value for a form or grid row: linked when the row carries an incoterm_location_id, legacy otherwise. */
export const incotermLocationFromFields = ({
  id,
  ...fields
}: IncotermLocationRowFields): SelectedIncotermLocation | null => {
  const location = legacyIncotermLocation(fields);
  return location && id ? { ...location, id } : location;
};

/** Fields for POST /pq, PATCH /pq/bulk-update: the id (when linked and enabled) plus the address strings. */
export const toIncotermLocationPayloadFields = (location: SelectedIncotermLocation) => ({
  ...(location.id && SEND_INCOTERM_LOCATION_ID ? { incoterm_location_id: location.id } : {}),
  incoterm_location_street: location.street ?? "",
  incoterm_location_postal_code: location.postal_code ?? "",
  incoterm_location_city: location.city ?? "",
  incoterm_location_country: location.country_code || location.country || "",
});

/** Row edits for the PQ review grid; null clears the location. */
export const toIncotermLocationRowEdits = (location: SelectedIncotermLocation | null) => ({
  incoterm_location_id: location?.id ?? null,
  incoterm_location_street: location?.street ?? "",
  incoterm_location_postal_code: location?.postal_code ?? "",
  incoterm_location_city: location?.city ?? "",
  incoterm_location_country: location ? location.country_code || location.country || "" : "",
});

/** Localised country name ("Germany" / "Deutschland"); falls back to the code. */
export const getCountryDisplayName = (code: string, locale: string): string => {
  try {
    return new Intl.DisplayNames([locale], { type: "region" }).of(code) ?? code;
  } catch {
    return code;
  }
};

export const hasPostalCode = (location: Pick<IncotermLocationAddress, "postal_code">): boolean =>
  trimmed(location.postal_code) !== "";
